/**
 * EvidenceDisclosure — expandable "Why am I seeing this?" panel.
 *
 * Renders the ``finding.evidence`` block returned by the backend
 * verbatim. Confidence is displayed *inside* the disclosure (per
 * Patch 35.5.1 spec) so the founder always sees the certainty attached
 * to the underlying facts.
 */
import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

import type { AkinexConfidence, AkinexEvidence } from "@/entities/akinex/api";

const CONFIDENCE_LABEL: Record<AkinexConfidence, string> = {
  very_high: "Very high confidence",
  high: "High confidence",
  moderate: "Moderate confidence",
  low: "Low confidence",
};

export interface EvidenceDisclosureProps {
  findingKey: string;
  evidence: AkinexEvidence;
  confidence: AkinexConfidence;
}

export function EvidenceDisclosure({
  findingKey,
  evidence,
  confidence,
}: EvidenceDisclosureProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const testIdRoot = `akinex-finding-${findingKey}-evidence`;

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={`${testIdRoot}-panel`}
        data-testid={`${testIdRoot}-toggle`}
        className="inline-flex items-center gap-1 text-xs text-[var(--md-fog)] hover:text-[var(--md-ivory-0)] transition"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5" aria-hidden />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" aria-hidden />
        )}
        <span>Why am I seeing this?</span>
      </button>

      {open ? (
        <div
          id={`${testIdRoot}-panel`}
          data-testid={`${testIdRoot}-panel`}
          className="mt-2 rounded-lg border border-white/5 bg-black/20 p-3 text-xs text-[var(--md-ivory-0)]"
        >
          <div
            data-testid={`${testIdRoot}-confidence`}
            className="mb-2 text-[11px] uppercase tracking-widest text-[var(--md-fog)]"
          >
            {CONFIDENCE_LABEL[confidence]}
          </div>

          {evidence.facts.length > 0 ? (
            <ul
              data-testid={`${testIdRoot}-facts`}
              className="space-y-1 font-mono text-[11px] leading-relaxed"
            >
              {evidence.facts.map((fact, i) => (
                <li key={i} className="text-[var(--md-ivory-0)]">
                  {formatFact(fact)}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[var(--md-fog)]">No structured facts provided.</p>
          )}

          {evidence.sources.length > 0 ? (
            <div
              data-testid={`${testIdRoot}-sources`}
              className="mt-2 flex flex-wrap items-center gap-x-2 text-[11px] text-[var(--md-fog)]"
              aria-label={`Sources: ${evidence.sources.join(", ")}`}
            >
              <span className="uppercase tracking-widest">Sources:</span>
              <span className="font-mono">{evidence.sources.join(", ")}</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function formatFact(fact: Record<string, unknown>): string {
  const entries = Object.entries(fact);
  if (entries.length === 0) return "—";
  return entries
    .map(([k, v]) => `${k}: ${formatValue(v)}`)
    .join(" · ");
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return "null";
  if (Array.isArray(v)) return `[${v.map(formatValue).join(", ")}]`;
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
